/*
  Blog post behaviour, loaded only by sections/main-blog-post.liquid. It used
  to sit in { % javascript % } tags, which Shopify bundles into a script that
  runs on every page.
*/

/* sections/main-blog-post.liquid: share bar, back to top */
(() => {
  /* Share bar: "Copy link" confirms in place; where the device has a share
     sheet (phones), "Share…" opens it and stands in for the icon row. */
  document.querySelectorAll('.blog-post-share').forEach((bar) => {
    const status = bar.querySelector('[data-share-status]');
    const nativeButton = bar.querySelector('[data-share-native]');
    const copyButton = bar.querySelector('[data-share-copy]');
    const url = copyButton?.dataset.url || window.location.href;

    if (nativeButton && navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      nativeButton.hidden = false;
      bar.classList.add('blog-post-share--native');
      nativeButton.addEventListener('click', () => {
        navigator.share({ title: document.title, url }).catch(() => {});
      });
    }

    if (!copyButton) return;
    const label = copyButton.querySelector('[data-share-copy-label]');
    let reset = 0;
    const copied = () => {
      copyButton.classList.add('is-copied');
      label.textContent = 'Link copied';
      status.textContent = 'Link copied to clipboard';
      clearTimeout(reset);
      reset = setTimeout(() => {
        copyButton.classList.remove('is-copied');
        label.textContent = 'Copy link';
        status.textContent = '';
      }, 2000);
    };
    const fallback = () => {
      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      const ok = document.execCommand('copy');
      field.remove();
      if (ok) copied();
    };
    copyButton.addEventListener('click', () => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(copied, fallback);
      } else {
        fallback();
      }
    });
  });

  /* Back to top: a floating button that appears once the reader is more
     than a screen in, and returns them (and keyboard focus) to the title. */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    /* Moved to <body> so no transformed parent can trap its fixed position. */
    document.body.append(backToTop);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const update = () => {
      frame = 0;
      backToTop.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.2);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
    backToTop.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      const title = document.querySelector('.blog-post-header h1');
      if (title) {
        title.setAttribute('tabindex', '-1');
        title.focus({ preventScroll: true });
      }
    });
  }
})();

/* blocks/_blog-post-content.liquid: link buttons, reading progress, videos, glossary */
(() => {
  /* A paragraph (or box) holding nothing but one or more short text links
     -- "Shop Gyuto Knives ->", "Browse Single Edged Knives" -- is a call to
     action, so it renders as buttons. Links that share a line with other
     words, image links and list items stay ordinary links. */
  document.querySelectorAll('.blog-post-content :is(p, div)').forEach((block) => {
    const links = [...block.children].filter((child) => child.tagName === 'A');
    if (!links.length || block.querySelector('img, div, p, ul, ol')) return;
    if (block.closest('.blog-post-product')) return;

    const squash = (text) => text.replace(/\s+/g, '');
    const linkText = links.map((link) => link.textContent).join('');
    if (squash(block.textContent) !== squash(linkText)) return;
    if (links.some((link) => link.textContent.trim().length > 60)) return;

    /* Inside a card (card grids, grade cards) it's the card's own link,
       set as a quiet "Shop ... ->" line rather than one more button. */
    if (block.parentElement.matches('.blog-post-grades > div, div:has(> div + div > h3:first-child) > div')) {
      block.classList.add('blog-post-content__more');
      return;
    }

    /* Outlined: the page keeps one filled button (the signup). */
    block.classList.add('blog-post-content__cta');
    links.forEach((link) => link.classList.add('button', 'button-secondary'));
  });

  /* Reading progress: starts filling on the first scroll and is full when
     the end of the article reaches the bottom of the screen, so the product
     strip and "Keep reading" below it don't count. */
  const postMeter = document.querySelector('.blog-post-meter');
  const readingContent = document.querySelector('.blog-post-content');
  if (postMeter && readingContent) {
    /* Moved to <body> so no transformed or stacking parent can trap it. */
    document.body.append(postMeter);
    let frame = 0;
    const header = document.getElementById('header-component');
    /* Takes the accent of the header row it sits under (gold under the
       navy menu bar), so it doesn't blend into that row. */
    const headerRow = header && [...header.querySelectorAll('.header__row')].filter((row) => row.offsetHeight).pop();
    const headerAccent = headerRow && getComputedStyle(headerRow).getPropertyValue('--color-primary').trim();
    if (headerAccent) postMeter.style.setProperty('--post-meter-color', headerAccent);
    const update = () => {
      frame = 0;
      /* Sits just under the sticky header, or at the top when there's none. */
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      postMeter.style.top = `${Math.round(headerBottom)}px`;
      const end = readingContent.getBoundingClientRect().bottom + window.scrollY - window.innerHeight;
      const read = end > 0 ? window.scrollY / end : 1;
      postMeter.style.setProperty('--post-meter', String(Math.min(1, Math.max(0, read))));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  /* Video thumbnails: pressing play swaps in the YouTube player. */
  document.querySelectorAll('.blog-post-video__play').forEach((button) => {
    button.addEventListener('click', () => {
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(button.dataset.videoId)}?autoplay=1&rel=0`;
      frame.title = button.dataset.videoTitle || 'Video';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      button.replaceWith(frame);
      frame.focus();
    });
  });

  /* Tap-to-define: the first time a glossary term (snippets/blog-glossary)
     appears in the article's running text it becomes a button that opens
     a short definition. Headings, links, buttons and cards are left alone. */
  const glossaryData = document.getElementById('BlogGlossary');
  const glossaryRoot = document.querySelector('.blog-post-content');
  if (glossaryData && glossaryRoot) {
    let terms = [];
    try {
      terms = JSON.parse(glossaryData.textContent);
    } catch (error) {
      terms = [];
    }
    const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const skip = 'a, button, h1, h2, h3, h4, h5, h6, th, caption, figcaption, nav, .blog-post-product, .blog-post-shop, .blog-post-glance > p:first-child, .blog-post-content__cta';
    const tip = document.createElement('div');
    tip.className = 'blog-term-tip';
    tip.id = 'BlogTermTip';
    tip.setAttribute('aria-live', 'polite');
    tip.hidden = true;
    document.body.append(tip);
    let openTerm = null;

    const close = () => {
      if (!openTerm) return;
      openTerm.setAttribute('aria-expanded', 'false');
      tip.hidden = true;
      openTerm = null;
    };
    const place = (button) => {
      const box = button.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 32);
      const left = Math.min(Math.max(16, box.left + box.width / 2 - width / 2), window.innerWidth - width - 16);
      tip.style.width = `${width}px`;
      tip.style.left = `${left + window.scrollX}px`;
      tip.style.top = `${box.bottom + window.scrollY + 8}px`;
    };
    const open = (button, entry) => {
      close();
      tip.replaceChildren();
      const name = document.createElement('p');
      name.className = 'blog-term-tip__term';
      name.textContent = entry.term;
      const text = document.createElement('p');
      text.className = 'blog-term-tip__text';
      text.textContent = entry.text;
      tip.append(name, text);
      tip.hidden = false;
      place(button);
      button.setAttribute('aria-expanded', 'true');
      openTerm = button;
    };

    const found = new Set();
    const link = () => {
      const nodes = [];
      const walker = document.createTreeWalker(glossaryRoot, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          node.parentElement.closest('p, li, td') && !node.parentElement.closest(skip)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT,
      });
      while (walker.nextNode()) nodes.push(walker.currentNode);
      terms.forEach((entry) => {
        if (found.has(entry.term)) return;
        const pattern = new RegExp(`(^|[^\\w-])(${entry.match.map(escape).join('|')})(?![\\w-])`, 'i');
        const index = nodes.findIndex((node) => pattern.test(node.nodeValue));
        if (index < 0) return;
        found.add(entry.term);
        const node = nodes[index];
        const match = node.nodeValue.match(pattern);
        const start = match.index + match[1].length;
        const word = node.splitText(start);
        const after = word.splitText(match[2].length);
        nodes.splice(index, 1, node, after);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'blog-term';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', 'BlogTermTip');
        button.textContent = word.nodeValue;
        word.replaceWith(button);
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          if (openTerm === button) close();
          else open(button, entry);
        });
      });
    };
    if (terms.length) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(link, { timeout: 2000 });
      else window.setTimeout(link, 1);
    }

    document.addEventListener('click', (event) => {
      if (openTerm && !tip.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openTerm) {
        const button = openTerm;
        close();
        button.focus();
      }
    });
    window.addEventListener('resize', () => openTerm && place(openTerm));
  }
})();
